import { Button } from "@/components/ui/button";
import { FictionalVehicle, fmt } from "@/hooks/useVehicleSim";
import { ArrowLeft, Check } from "lucide-react";
import vehicleImages from "./vehicleImages";

interface Props {
  vehicles: FictionalVehicle[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  onContinue: () => void;
  onBack: () => void;
}

export default function VehicleMarketplace({ vehicles, selectedKey, onSelect, onContinue, onBack }: Props) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="font-heading text-lg font-bold text-foreground">Vehicle Marketplace</h2>
        <p className="text-sm text-muted-foreground">Browse available vehicles. Select one to see detailed information.</p>
      </div>

      <div className="space-y-3">
        {vehicles.map(v => (
          <button
            key={v.vehicle_key}
            onClick={() => onSelect(v.vehicle_key)}
            className={`w-full text-left rounded-2xl border overflow-hidden transition-all duration-200 ${
              selectedKey === v.vehicle_key
                ? 'glass-card glow-border ring-2 ring-primary/30'
                : 'glass-card hover:shadow-card-hover'
            }`}
          >
            {vehicleImages[v.vehicle_key] && (
              <img src={vehicleImages[v.vehicle_key]} alt={v.vehicle_name} className="w-full h-64 object-cover" />
            )}
            <div className="p-5 space-y-2">
              <div className="flex items-start justify-between">
                <div className="space-y-1 flex-1">
                  <p className="font-heading font-semibold text-foreground">{v.vehicle_name}</p>
                  <p className="text-xs text-muted-foreground">{v.description}</p>
                </div>
                {selectedKey === v.vehicle_key && <Check className="w-5 h-5 text-primary shrink-0 mt-1" />}
              </div>

              <div className="flex items-baseline gap-2">
                <p className="text-lg font-bold text-foreground">{fmt(v.purchase_price)}</p>
                <span className="text-xs text-muted-foreground">Est. {fmt(v.monthly_payment_low)}–{fmt(v.monthly_payment_high)}/mo</span>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {v.tags.map(tag => (
                  <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{tag}</span>
                ))}
              </div>

              <div className="grid grid-cols-4 gap-2 pt-1 text-[10px]">
                <div className="text-center"><div className="text-muted-foreground">Practical</div><div className="font-bold text-foreground">{v.practicality_rating}/10</div></div>
                <div className="text-center"><div className="text-muted-foreground">Status</div><div className="font-bold text-foreground">{v.status_rating}/10</div></div>
                <div className="text-center"><div className="text-muted-foreground">Efficiency</div><div className="font-bold text-foreground">{v.efficiency_rating}/10</div></div>
                <div className="text-center"><div className="text-muted-foreground">Reliable</div><div className="font-bold text-foreground">{v.reliability_rating}/10</div></div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="gap-2"><ArrowLeft className="w-4 h-4" /> Back</Button>
        <Button onClick={onContinue} disabled={!selectedKey} className="flex-1">View Details</Button>
      </div>
    </div>
  );
}
